import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard, FolderKanban, FileCheck2, ScrollText, Image as ImageIcon,
  Sparkles, BookOpen, LogOut, Search, Send, Upload, ShieldCheck, AlertTriangle,
  CheckCircle2, ChevronRight, Building2, Hash, BarChart3, MessageSquareText, Loader2, X,
  Inbox, Link as LinkIcon, Menu, LogIn, FileText
} from "lucide-react";
import { API } from "../lib/api";
import { fmtAUD, fmtMoney, fmtBytes } from "../lib/format";

export function CETVerifier() {
  /* Standalone CET v2.1 Verifier — upload a council CET workbook, get the same
     line-by-line verification, P50 vs P90 panel, cost-band check + escalation
     workings as the per-DM modal inside the ingest job. No login required. */
  const [file, setFile] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const onUpload = async (f) => {
    if (!f) return;
    setFile(f); setData(null); setErr(null); setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${API}/tools/cet-verifier`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to verify CET workbook.");
    } finally {
      setLoading(false);
    }
  };

  const cet = data?.cet;
  const cetLines = cet?.lines || [];
  const realLines = cetLines.filter(l => !l.is_parent);
  const visibleLines = showAll ? cetLines : realLines;

  return (
    <div className="space-y-6" data-testid="cet-verifier-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">CET Verifier</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
          Upload a NSWRA CET v2.1 workbook to see line-by-line variance against the published rate
          schedule, the P50 vs P90 outturn comparison, the cost-band check (PM / Design / Contingency
          / Escalation), and a plain-English explainer of each component.
        </p>
      </div>

      <div className="card p-5">
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-2">Upload CET workbook (.xlsx)</div>
          <input
            type="file"
            accept=".xlsx,.xlsm"
            data-testid="cet-verifier-file-input"
            onChange={(e) => onUpload(e.target.files?.[0])}
            className="block w-full text-sm text-slate-600 file:mr-4 file:px-4 file:py-2 file:rounded-md file:border-0 file:bg-slate-900 file:text-white file:cursor-pointer hover:file:bg-slate-700"
          />
          {file && (
            <div className="mt-2 text-[11.5px] font-mono text-slate-500">Selected: {file.name} · {(file.size/1024).toFixed(1)} KB</div>
          )}
        </label>
      </div>

      {loading && (
        <div className="card p-5 flex items-center gap-3 text-sm text-slate-600">
          <Loader2 className="w-4 h-4 animate-spin"/> Parsing CET workbook…
        </div>
      )}
      {err && <div className="card p-5 text-sm text-red-600">{err}</div>}

      {cet && (
        <div className="space-y-4" data-testid="cet-verifier-results">
          {/* Headline strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
            <div className="bg-slate-50 border border-slate-200 rounded p-2">
              <div className="text-[9px] uppercase tracking-wider text-slate-500">CET direct</div>
              <div className="font-mono font-semibold text-slate-800">{fmtMoney(cet.direct_total)}</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
              <div className="text-[9px] uppercase tracking-wider text-emerald-700">CET P50 outturn · eligible</div>
              <div className="font-mono font-semibold text-emerald-800">{fmtMoney(cet.outturn_p50)}</div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded p-2">
              <div className="text-[9px] uppercase tracking-wider text-slate-500">CET P90 outturn</div>
              <div className="font-mono font-semibold text-slate-800">{fmtMoney(cet.outturn_p90)}</div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded p-2">
              <div className="text-[9px] uppercase tracking-wider text-slate-500">P90 − P50 spread</div>
              <div className="font-mono font-semibold text-slate-800">{fmtMoney(cet.p50_p90_spread_aud)} {cet.p50_p90_spread_pct != null && <span className="text-slate-500 font-normal text-[10px]">({cet.p50_p90_spread_pct}%)</span>}</div>
            </div>
          </div>

          {/* Workbook metadata */}
          <div className="card p-4 text-[12px] text-slate-700">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><span className="text-slate-500">File:</span> <span className="font-mono">{cet.filename}</span></div>
              <div><span className="text-slate-500">LGA:</span> <span className="font-mono">{cet.lga || "—"}</span></div>
              <div><span className="text-slate-500">FY:</span> <span className="font-mono">{cet.project_fy || "—"}</span></div>
              <div><span className="text-slate-500">Costed lines:</span> <span className="font-mono">{realLines.length}</span></div>
            </div>
          </div>

          {/* Line items table */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Submitted vs Verified · per line</div>
              <button
                type="button"
                className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline"
                onClick={() => setShowAll(s => !s)}
              >{showAll ? "Hide zero-rate rows" : "Show all rows"}</button>
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded">
              <table className="w-full text-[10.5px]">
                <thead>
                  <tr className="bg-slate-200 text-left text-slate-700">
                    <th className="p-1.5 font-semibold" rowSpan={2}>Ref / treatment</th>
                    <th className="p-1.5 font-semibold text-right" rowSpan={2}>Qty</th>
                    <th className="p-1.5 font-semibold" rowSpan={2}>Unit</th>
                    <th className="p-1.5 font-semibold text-center border-l border-slate-300" colSpan={2}>Submitted</th>
                    <th className="p-1.5 font-semibold text-center border-l border-slate-300" colSpan={2}>Verified (CET v2.1)</th>
                    <th className="p-1.5 font-semibold text-center border-l border-slate-300" colSpan={2}>Variance</th>
                  </tr>
                  <tr className="bg-slate-100 text-left text-slate-600">
                    <th className="p-1.5 font-semibold text-right border-l border-slate-300">Rate</th>
                    <th className="p-1.5 font-semibold text-right">Direct</th>
                    <th className="p-1.5 font-semibold text-right border-l border-slate-300">Rate</th>
                    <th className="p-1.5 font-semibold text-right">Direct</th>
                    <th className="p-1.5 font-semibold text-right border-l border-slate-300">%</th>
                    <th className="p-1.5 font-semibold text-right">$ effect</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleLines.map((l, i) => {
                    const v = l.verification || {};
                    const tone = v.status === "match" ? "bg-emerald-50"
                      : v.status === "above" ? "bg-red-50"
                      : v.status === "below" ? "bg-amber-50"
                      : v.status === "lump_sum" ? "bg-blue-50"
                      : l.is_parent ? "bg-slate-50 italic text-slate-500"
                      : "";
                    return (
                      <tr key={i} className={`border-t border-slate-100 ${tone}`} title={v.decision_hint || ""}>
                        <td className="p-1.5">
                          <div className="font-mono text-[10px] text-slate-500">{l.ref}</div>
                          <div className="truncate max-w-[260px]">{l.treatment}</div>
                        </td>
                        <td className="p-1.5 font-mono text-right">{l.qty != null ? Number(l.qty).toLocaleString() : "—"}</td>
                        <td className="p-1.5">{l.unit || "—"}</td>
                        <td className="p-1.5 font-mono text-right border-l border-slate-200">{l.direct_rate != null ? fmtMoney(l.direct_rate) : "—"}</td>
                        <td className="p-1.5 font-mono text-right">{fmtMoney(l.direct_total)}</td>
                        <td className="p-1.5 font-mono text-right border-l border-slate-200">{v.ref_rate != null ? fmtMoney(v.ref_rate) : "—"}</td>
                        <td className="p-1.5 font-mono text-right">{v.verified_direct != null ? fmtMoney(v.verified_direct) : "—"}</td>
                        <td className="p-1.5 font-mono text-right border-l border-slate-200">{v.variance_pct != null ? `${v.variance_pct > 0 ? "+" : ""}${v.variance_pct}%` : (v.status || "—")}</td>
                        <td className="p-1.5 font-mono text-right">{v.delta_direct != null ? fmtMoney(v.delta_direct) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cost-band check */}
          {cet.cost_bands && (
            <div className="card p-4">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700 mb-2">Cost-band check</div>
              <table className="w-full text-[11px]">
                <thead className="bg-slate-100"><tr className="text-left">
                  <th className="p-1.5">Component</th><th className="p-1.5 text-right">Actual %</th>
                  <th className="p-1.5 text-right">Allowed band</th><th className="p-1.5">Status</th>
                </tr></thead>
                <tbody>
                  {Object.entries(cet.cost_bands).filter(([k]) => k !== "error").map(([k, v]) => {
                    const band = v.band || cet.cost_band_thresholds[k];
                    const tone = v.status === "within" ? "bg-emerald-50"
                      : v.status === "above" ? "bg-red-50" : "bg-amber-50";
                    return (
                      <tr key={k} className={`${tone} border-t border-slate-100`}>
                        <td className="p-1.5 capitalize">{k.replace(/_/g," ").replace(" pct of direct","")}</td>
                        <td className="p-1.5 font-mono text-right">{v.actual_pct}%</td>
                        <td className="p-1.5 font-mono text-right">{band.min}%–{band.max}%</td>
                        <td className="p-1.5 uppercase text-[10px] tracking-wider font-semibold">{v.status}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Explainers */}
          {cet.explainers && (
            <details className="card p-4">
              <summary className="cursor-pointer text-[10px] uppercase tracking-wider font-bold text-slate-700">
                Why P50, contingency, escalation? (click to expand)
              </summary>
              <dl className="mt-3 text-[11.5px] text-slate-700 space-y-2">
                {Object.entries(cet.explainers).map(([k, t]) => (
                  <div key={k}>
                    <dt className="font-semibold capitalize text-slate-800">{k.replace(/_/g," ")}:</dt>
                    <dd className="text-slate-600">{t}</dd>
                  </div>
                ))}
              </dl>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

